import { useState, useRef, useEffect } from "react";
import { ReactComponent as ArrowDownIcon } from '../../assets/icons/arrow-down-icon.svg';
import { ReactComponent as ArrowLeftIcon } from '../../assets/icons/arrow-left-icon.svg';
import { ReactComponent as ArrowRightIcon } from '../../assets/icons/arrow-right-icon.svg';
import { ReactComponent as FilterCalendarIcon } from '../../assets/icons/filter-calendar-icon.svg';

const MONTHS = [
  "Январь", "Февраль", "Март", "Апрель",
  "Май", "Июнь", "Июль", "Август",
  "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
];

const COLORS = {
  green: "#7CC805",
  black: "#010101",
  lightGray: "#F7F7F8",
  white: "#FFFFFF",
};

export default function MonthYearPicker({ value, onChange, placeholder = "Select month & year", disableFuture = false, minDate, maxDate, isCalendarIcon = false, readOnly = false }) {
  // value: "YYYY-MM" or ""
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => {
    if (value) return parseInt(value.split("-")[0]);
    return new Date().getFullYear();
  });
  const ref = useRef(null);

  const selectedYear = value ? parseInt(value.split("-")[0]) : null;
  const selectedMonth = value ? parseInt(value.split("-")[1]) : null; // 1-indexed

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Sync viewYear when value changes externally
  useEffect(() => {
    if (value) setViewYear(parseInt(value.split("-")[0]));
  }, [value]);

  const handleMonthClick = (monthIndex) => {
    // monthIndex: 0-indexed
    const month = String(monthIndex + 1).padStart(2, "0");
    onChange(`${viewYear}-${month}`);
    setOpen(false);
  };

  const displayValue = value
    ? `${MONTHS[selectedMonth - 1]} ${selectedYear}`
    : placeholder;

  const isSelected = (monthIndex) =>
    selectedYear === viewYear && selectedMonth === monthIndex + 1;

  const isCurrentMonth = (monthIndex) => {
    const now = new Date();
    return now.getFullYear() === viewYear && now.getMonth() === monthIndex;
  };

  const isFutureMonth = (monthIndex) => {
    if (!disableFuture) return false;
    const now = new Date();
    return viewYear > now.getFullYear() || (viewYear === now.getFullYear() && monthIndex > now.getMonth());
  };

  const isOutOfRange = (monthIndex) => {
    const month = monthIndex + 1;
    if (minDate) {
      const [minY, minM] = minDate.split('-').map(Number);
      if (viewYear < minY || (viewYear === minY && month < minM)) return true;
    }
    if (maxDate) {
      const [maxY, maxM] = maxDate.split('-').map(Number);
      if (viewYear > maxY || (viewYear === maxY && month > maxM)) return true;
    }
    return false;
  };

  const minYear = minDate ? parseInt(minDate.split('-')[0]) : null;
  const maxYear = maxDate ? parseInt(maxDate.split('-')[0]) : null;
  const prevYearDisabled = minYear !== null && viewYear <= minYear;
  const nextYearDisabled = (maxYear !== null && viewYear >= maxYear) || (disableFuture && viewYear >= new Date().getFullYear());

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block", height: "100%" }}>
      {/* Trigger */}
      <button
        onClick={() => { if (!readOnly) setOpen((prev) => !prev); }}
        disabled={readOnly}
        className="filter-select"
        style={{
          transition: "border-color 0.15s",
          cursor: readOnly ? "default" : "pointer",
          opacity: readOnly ? 0.7 : 1,
        }}
      >
        {
          isCalendarIcon ?
            <FilterCalendarIcon size={22} color="rgba(137, 137, 137, 1)"/>
              : <span className="filter-select-label">Период:</span>
        }

        <span className="filter-select-option">{displayValue}</span>
        {!readOnly && <ArrowDownIcon size={24} color='#7CC805'/>}
      </button>

      {/* Dropdown */}
      {open && !readOnly && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            zIndex: 1000,
            background: COLORS.white,
            border: `1.5px solid #E5E7EB`,
            borderRadius: 12,
            boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
            padding: 16,
            minWidth: 200,
          }}
        >
          {/* Year Navigation */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 14,
            }}
          >
          {/* <button onClick={() => changeMonth(-1)}><ArrowLeftIcon size={12} color="#6D6D6D"/></button>
          <button onClick={() => changeMonth(1)}><ArrowRightIcon size={12} color="#6D6D6D"/></button> */}
            <button onClick={() => !prevYearDisabled && setViewYear((y) => y - 1)} style={{...navBtnStyle, opacity: prevYearDisabled ? 0.35 : 1, cursor: prevYearDisabled ? 'not-allowed' : 'pointer'}}
              onMouseEnter={(e) => { if (!prevYearDisabled) e.currentTarget.style.background = "#e8f5d0"; }}
              onMouseLeave={(e) => { if (!prevYearDisabled) e.currentTarget.style.background = COLORS.lightGray; }}
            >
              <ArrowLeftIcon size={12} color="#6D6D6D"/>
            </button>

            <span style={{ fontWeight: 700, fontSize: 15, color: COLORS.black }}>
              {viewYear}
            </span>

            <button onClick={() => !nextYearDisabled && setViewYear((y) => y + 1)} style={{...navBtnStyle, opacity: nextYearDisabled ? 0.35 : 1, cursor: nextYearDisabled ? 'not-allowed' : 'pointer'}}
              onMouseEnter={(e) => { if (!nextYearDisabled) e.currentTarget.style.background = "#e8f5d0"; }}
              onMouseLeave={(e) => { if (!nextYearDisabled) e.currentTarget.style.background = COLORS.lightGray; }}
            >
              <ArrowRightIcon size={12} color="#6D6D6D"/>
            </button>
          </div>

          {/* Month Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 6,
            }}
          >
            {MONTHS.map((month, index) => {
              const selected = isSelected(index);
              const current = isCurrentMonth(index);
              const disabled = isFutureMonth(index) || isOutOfRange(index);

              return (
                <button
                  key={month}
                  onClick={() => !disabled && handleMonthClick(index)}
                  disabled={disabled}
                  style={{
                    padding: "8px 4px",
                    borderRadius: 8,
                    border: current && !selected ? `1.5px solid ${COLORS.green}` : "1.5px solid transparent",
                    background: selected ? COLORS.green : COLORS.lightGray,
                    color: selected ? COLORS.white : disabled ? "#c5c5c5" : COLORS.black,
                    fontWeight: selected ? 700 : current ? 600 : 400,
                    fontSize: 13,
                    cursor: disabled ? "not-allowed" : "pointer",
                    transition: "background 0.15s, color 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    if (!selected && !disabled) e.currentTarget.style.background = "#e8f5d0";
                  }}
                  onMouseLeave={(e) => {
                    if (!selected && !disabled) e.currentTarget.style.background = COLORS.lightGray;
                  }}
                >
                  {month.slice(0, 3)}
                </button>
              );
            })}
          </div>

          {/* Clear */}
          {value && (
            <button
              onClick={() => { setOpen(false); }}
              className="cancel-button"
              style={{
                marginTop: 12,
                width: "100%",
                padding: "6px",
                fontSize: 12,
              }}
            >
                Отмена
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const navBtnStyle = {
  background: "#F7F7F8",
  border: "none",
  borderRadius: 6,
  width: 30,
  height: 30,
  cursor: "pointer",
  fontSize: 18,
  color: "#010101",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 1,
};